/**
 * Custom hook for PDF generation in FarmDetailsModal.
 * Extracted from FarmDetailsModal.tsx.
 */

import { useState, useCallback } from "react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { FarmDetailsPDF } from "../../pdf/FarmDetailsPDF";
import {
  fetchHeaderLogo,
  convertImageToJpegBase64,
  convertImageToDataUrl,
  generateVideoThumbnail,
} from "./FarmDetailsModal/pdf/FarmDetailsModal.pdf.media";
import {
  collectAttachmentUrls,
  processAttachments,
  organizeAttachmentContent,
} from "./FarmDetailsModal/pdf/FarmDetailsModal.pdf.attachments";
import { fetchTrainingProgressData } from "./FarmDetailsModal/pdf/FarmDetailsModal.pdf.training";
import { buildAndDownloadMergedPdf } from "./FarmDetailsModal/pdf/FarmDetailsModal.pdf.build";
import { generateMapImage } from "../../../utils/mapImageGenerator";
import { formatCoordinates } from "../../../utils/mapUtils";
import {
  OrganizationDynamicPropertyDto,
  DynamicPropertyTypeDto,
} from "../../../api/types/dtos/dynamicPropertyTypes";

interface UsePdfGenerationProps {
  orgDetails: any;
  cityName?: string;
  corregimientoName?: string;
  veredaName?: string;
  regionName?: string;
  farmLocation?: any;
  sortedApprovedLevels: [string, any[]][];
  inspectedUserPhones?: string[];
  ownOrgId?: string;
  organizationId: string;
  organizationDynamicProperties?: {
    data: OrganizationDynamicPropertyDto[];
  };
  dynamicPropertyTypes?: {
    data: DynamicPropertyTypeDto[];
  };
  pdfHeaderTitle: string;
  pdfHeaderSubtitle: string;
  pdfSectionChallenges: boolean;
  pdfSectionTrainings: boolean;
  pdfSectionAttachments: boolean;
  formsMap?: Record<string, any>;
  answersMap?: Record<string, any[]>;
  showSensitiveData?: boolean;
}

interface UsePdfGenerationResult {
  isGeneratingPdf: boolean;
  handleDownloadPDF: () => Promise<void>;
}

/**
 * Get dynamic header logo URL from properties
 */
function getDynamicHeaderLogoUrl(
  organizationDynamicProperties: OrganizationDynamicPropertyDto[] | undefined,
  dynamicPropertyTypes: DynamicPropertyTypeDto[] | undefined,
): string | null {
  if (organizationDynamicProperties) {
    const headerLogoProperty = organizationDynamicProperties.find(
      (prop) => prop.propertyTypeName === "pdf_section_header_logo",
    );
    if (headerLogoProperty?.value) {
      return headerLogoProperty.value;
    }
  }

  if (dynamicPropertyTypes) {
    const headerLogoType = dynamicPropertyTypes.find(
      (type) => type.name === "pdf_section_header_logo",
    );
    if (headerLogoType?.defaultValue) {
      return headerLogoType.defaultValue;
    }
  }

  return null;
}

/**
 * Hook for PDF generation
 */
export function usePdfGeneration({
  orgDetails,
  cityName,
  corregimientoName,
  veredaName,
  regionName,
  farmLocation,
  sortedApprovedLevels,
  inspectedUserPhones,
  ownOrgId,
  organizationId,
  organizationDynamicProperties,
  dynamicPropertyTypes,
  pdfHeaderTitle,
  pdfHeaderSubtitle,
  pdfSectionChallenges,
  pdfSectionTrainings,
  pdfSectionAttachments,
  formsMap = {},
  answersMap = {},
  showSensitiveData = false,
}: UsePdfGenerationProps): UsePdfGenerationResult {
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  const handleDownloadPDF = useCallback(async () => {
    if (!orgDetails) return;

    setIsGeneratingPdf(true);

    const data = organizationDynamicProperties?.data;
    const types = dynamicPropertyTypes?.data;

    // Get header logo URL
    const dynamicHeaderLogoUrl = getDynamicHeaderLogoUrl(data, types);
    const headerLogoDataUrl = await fetchHeaderLogo(dynamicHeaderLogoUrl);

    // Process logo
    let processedLogo: string | null = orgDetails.logo;
    if (
      orgDetails.logo &&
      typeof orgDetails.logo === "string" &&
      orgDetails.logo.match(/\.png($|\?)/i)
    ) {
      try {
        const cacheBustedUrl = `${orgDetails.logo}?_t=${new Date().getTime()}`;
        processedLogo = await convertImageToJpegBase64(cacheBustedUrl);
      } catch (e) {
        console.error("Failed to convert PNG logo, falling back to original:", e);
        processedLogo = orgDetails.logo;
      }
    }

    // Process attachments
    const attachmentUrls = collectAttachmentUrls(sortedApprovedLevels, answersMap);
    const attachmentResults = await processAttachments(
      attachmentUrls,
      generateVideoThumbnail,
      convertImageToDataUrl
    );
    const { contentTypes: attachmentContentTypes, videoThumbnails, imageDataUrls, attachedPdfs } =
      organizeAttachmentContent(attachmentResults);

    // Fetch training progress data
    const trainingProgressData = await fetchTrainingProgressData(ownOrgId, organizationId);

    // Generate map image
    let mapImageDataUrl: string | null = null;
    let mapCoordinates: string | null = null;
    if (farmLocation && farmLocation.latitude && farmLocation.longitude) {
      try {
        mapImageDataUrl = await generateMapImage(
          farmLocation.latitude,
          farmLocation.longitude,
          (farmLocation as any).area,
        );
        mapCoordinates = formatCoordinates(
          farmLocation.latitude,
          farmLocation.longitude,
        );
      } catch (error) {
        console.error("Error generating map image:", error);
      }
    }

    try {
      await buildAndDownloadMergedPdf(
        <FarmDetailsPDF
          orgDetails={{ ...orgDetails, logo: processedLogo }}
          cityName={cityName}
          corregimientoName={corregimientoName}
          veredaName={veredaName}
          regionName={regionName}
          sortedLevels={sortedApprovedLevels}
          sortedApprovedLevels={sortedApprovedLevels}
          farmItemsMap={{}}
          inspectedUserPhones={inspectedUserPhones}
          headerLogoDataUrl={headerLogoDataUrl}
          attachmentContentTypes={attachmentContentTypes}
          videoThumbnails={videoThumbnails}
          imageDataUrls={imageDataUrls}
          pdfHeaderTitle={pdfHeaderTitle}
          pdfHeaderSubtitle={pdfHeaderSubtitle}
          pdfSectionChallenges={pdfSectionChallenges}
          pdfSectionTrainings={pdfSectionTrainings}
          pdfSectionAttachments={pdfSectionAttachments}
          mapImageDataUrl={mapImageDataUrl}
          mapCoordinates={mapCoordinates}
          trainingProgressData={trainingProgressData}
          formsMap={formsMap}
          answersMap={answersMap}
          showSensitiveData={showSensitiveData}
        />,
        attachedPdfs,
        `${orgDetails.idNumber} - ${pdfHeaderSubtitle || "Hoja de Vida"} - ${format(new Date(), "MMM dd yyyy", { locale: es })}.pdf`,
      );
    } catch (error) {
      console.error("Error generating or merging PDF:", error);
    } finally {
      setIsGeneratingPdf(false);
    }
  }, [
    orgDetails,
    cityName,
    corregimientoName,
    veredaName,
    regionName,
    farmLocation,
    sortedApprovedLevels,
    inspectedUserPhones,
    ownOrgId,
    organizationId,
    organizationDynamicProperties,
    dynamicPropertyTypes,
    pdfHeaderTitle,
    pdfHeaderSubtitle,
    pdfSectionChallenges,
    pdfSectionTrainings,
    pdfSectionAttachments,
    formsMap,
    answersMap,
    showSensitiveData,
  ]);

  return {
    isGeneratingPdf,
    handleDownloadPDF,
  };
}
